import { emitVacationPackageExtraCurrencyAudit } from "../vacationPackageRoomAgentMarkup.js";

const BAGGAGE_TYPE_TOKENS = ["BAGGAGE", "LUGGAGE", "TROLLEY", "CARRY", "CABIN"];

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function round2(v) {
  return Math.round(safeNum(v) * 100) / 100;
}

function normalizeParams(params) {
  if (params == null) return [];
  if (Array.isArray(params)) return params;
  if (typeof params === "object" && params.name != null) return [params];
  return [];
}

/**
 * לפי LDS: תוספת/כבודה מגיעה במטבע הדיל — USD או EUR או GBP.
 */
export function normalizeVpLdsDealFareCurrency(raw) {
  const s = String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  if (s === "USD" || s === "EUR" || s === "GBP") return s;
  return "USD";
}

/** שער ציטוט קטלוג: מטבע אחיד → ₪ לאותו מטבע (כמו באפשרות AppContext.currencyRate). */
export function baggageSupplierForeignUnitToIls(isoNormalized, exchangeRates) {
  const map = exchangeRates || {};
  const cur = isoNormalized === "EUR" || isoNormalized === "GBP" ? isoNormalized : "USD";
  const direct = safeNum(map[cur]);
  if (direct > 0) return { rate: direct, via: cur };
  const fallbackUsd = safeNum(map.USD);
  if (fallbackUsd > 0) return { rate: fallbackUsd, via: `${cur}_fallback_USD` };
  return { rate: 1, via: `${cur}_neutral_1` };
}

export function isBaggageSupplement(supplement) {
  const typeRaw = String(supplement?.type || supplement?.Type || "").toUpperCase();
  if (BAGGAGE_TYPE_TOKENS.some((token) => typeRaw.includes(token))) return true;
  const text = String(
    supplement?.desc ||
      supplement?.description ||
      supplement?.titleLine ||
      ""
  ).toUpperCase();
  return ["CHECKED BAGGAGE", "CARRY-ON", "CABIN BAGGAGE"].some((token) =>
    text.includes(token)
  );
}

export function resolveSupplementQuantity(supplement) {
  const direct = [
    supplement?.quantity,
    supplement?.qty,
    supplement?.units,
    supplement?.unitCount,
    supplement?.count,
  ];
  for (const v of direct) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  for (const p of normalizeParams(supplement?.params)) {
    const name = String(p?.name || "").toLowerCase();
    if (!["quantity", "qty", "units", "count"].includes(name)) continue;
    const n = Number(p?.value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 1;
}

/**
 * @param baggageSupplierFareAuthoritativeIls רק למסלולים ישנים: fare כבר בש"ח (נדיר מחוץ ל-VP)
 * @param vpBaggageApplyLdsDealCurrencyFx חבילות נופש LDS: fare במטבע fareDetails.currency (USD/EUR/GBP)
 * @param baggageFlatAuthoritativeIls legacy only — GC BAGGAGE_FEE_FLAT is always USD×USD rate
 */
function supplementPriceAuditSnapshot({
  fareUsd,
  currencyRateUsd,
  baggageFeeFlatUsd,
  supplement,
  baggageFlatAuthoritativeIls = false,
  baggageSupplierFareAuthoritativeIls = false,
  vpBaggageApplyLdsDealCurrencyFx = false,
  vpLdsDealFareCurrencyIso = "",
  exchangeRates = null,
  traceExtra = {},
}) {
  const usdToIlsLegacy = safeNum(currencyRateUsd);
  const rawFare = safeNum(fareUsd);
  const baggage = isBaggageSupplement(supplement);

  /** לשורות לא-מזוודה: מתנהג כמו בעבר USD×שער (+ מסלול authoritative נדיר). */
  if (!baggage) {
    const baseNonBaggage = baggageSupplierFareAuthoritativeIls
      ? rawFare
      : rawFare * usdToIlsLegacy;
    const totalIls = round2(baseNonBaggage);
    const trace = buildAuditEnvelope({
      isBaggage: false,
      totalIls,
      rawFare,
      supplierCurrencyResolved: "USD",
      supplierFxApplied: usdToIlsLegacy,
      baseIls: totalIls,
      baggageFeeIls: 0,
      flatRaw: 0,
      qty: 1,
      baggageFlatAuthoritativeIls,
      baggageSupplierFareAuthoritativeIls,
      vpBaggageApplyLdsDealCurrencyFx: false,
      supplement,
      traceExtra,
      usdToIlsLegacyCatalogField: usdToIlsLegacy,
    });
    return { totalIls, trace };
  }

  const quantity = resolveSupplementQuantity(supplement);
  const qty = safeNum(quantity || 1);
  const flatRaw = safeNum(baggageFeeFlatUsd);
  const baggageFeeIls = baggageFlatAuthoritativeIls
    ? flatRaw * qty
    : flatRaw * usdToIlsLegacy * qty;

  let baseIls;
  let supplierCurrencyResolved;
  /** שער מאוחד בסגנון "יחידות מטבע דיל × שער זה → ₪" */
  let supplierFxApplied;
  /** איך נבחר השער לשורת המזוודה */
  let supplierFxExplain;

  if (vpBaggageApplyLdsDealCurrencyFx && exchangeRates) {
    supplierCurrencyResolved = normalizeVpLdsDealFareCurrency(
      vpLdsDealFareCurrencyIso
    );
    const fx = baggageSupplierForeignUnitToIls(
      supplierCurrencyResolved,
      exchangeRates
    );
    supplierFxApplied = fx.rate;
    supplierFxExplain = fx.via;
    baseIls = rawFare * supplierFxApplied;
  } else if (baggageSupplierFareAuthoritativeIls) {
    supplierCurrencyResolved = "ILS";
    supplierFxApplied = 1;
    supplierFxExplain = "legacy_authoritative_ils_on_fare_field";
    baseIls = rawFare;
  } else {
    supplierCurrencyResolved = "USD";
    supplierFxApplied = usdToIlsLegacy;
    supplierFxExplain = "legacy_usd_rate_field";
    baseIls = rawFare * usdToIlsLegacy;
  }

  const totalIls = round2(baseIls + baggageFeeIls);

  const trace = buildAuditEnvelope({
    isBaggage: true,
    totalIls,
    rawFare,
    supplierCurrencyResolved,
    supplierFxApplied: round2(supplierFxApplied),
    supplierFxSelectionNote: supplierFxExplain,
    baseIls: round2(baseIls),
    baggageFeeIls: round2(baggageFeeIls),
    flatRaw,
    qty,
    baggageFlatAuthoritativeIls,
    baggageSupplierFareAuthoritativeIls,
    vpBaggageApplyLdsDealCurrencyFx: !!vpBaggageApplyLdsDealCurrencyFx,
    supplement,
    traceExtra,
    usdToIlsLegacyCatalogField: usdToIlsLegacy,
  });

  return { totalIls, trace };
}

function buildAuditEnvelope({
  isBaggage,
  totalIls,
  rawFare,
  supplierCurrencyResolved,
  supplierFxApplied,
  supplierFxSelectionNote,
  baseIls,
  baggageFeeIls,
  flatRaw,
  qty,
  baggageFlatAuthoritativeIls,
  baggageSupplierFareAuthoritativeIls,
  vpBaggageApplyLdsDealCurrencyFx,
  supplement,
  traceExtra,
  usdToIlsLegacyCatalogField,
}) {
  const supplierFareWasFxConverted =
    isBaggage && rawFare !== 0 ? supplierFxApplied !== 1 : false;

  return {
    auditTag: "[VACATION_PACKAGE_EXTRA_CURRENCY_AUDIT]",
    computedAtIso: typeof Date !== "undefined" ? new Date().toISOString() : null,
    extraType:
      String(supplement?.type ?? supplement?.Type ?? "").toUpperCase() || "UNKNOWN",
    isBaggage,
    quantity: qty,
    ldsDealFareCurrencyNormalized: supplierCurrencyResolved,
    rawSupplierFareInLdsDealCurrency: rawFare,
    /** שער מהמפה הרב-מטבעית (יחידות מטבע דיל לשקל תצוגתי) — לא localStorage catalogue */
    supplierFxDealCurrencyToIls: round2(supplierFxApplied),
    supplierFxHowChosen: supplierFxSelectionNote ?? null,
    vpBaggageLdsFareUsesDealCurrencyFx: !!vpBaggageApplyLdsDealCurrencyFx,
    legacyUsdCatalogRateField: round2(usdToIlsLegacyCatalogField ?? 1),
    supplierFareLegacyAuthoritativeIlsMode:
      isBaggage && !!baggageSupplierFareAuthoritativeIls,
    normalizedSupplierPortionIls:
      !isBaggage || rawFare !== 0 || baseIls !== 0 ? round2(baseIls) : 0,
    gcBaggageFlatRaw: flatRaw,
    gcFlatInterpretationForVpBaggage:
      baggageFlatAuthoritativeIls && isBaggage ? "ILS" : "USD",
    flatAuthoritativeIlsMode: baggageFlatAuthoritativeIls && isBaggage,
    normalizedFlatPortionIls: isBaggage ? round2(baggageFeeIls) : 0,
    flatWasFxConverted:
      isBaggage && flatRaw !== 0 ? !baggageFlatAuthoritativeIls : false,
    normalizedTotalIls: round2(totalIls),
    supplierFareWasFxConverted,
    wasConvertedEitherLeg:
      supplierFareWasFxConverted ||
      (isBaggage && flatRaw !== 0 && !baggageFlatAuthoritativeIls),
    source: traceExtra.source ?? "computeSupplementPriceIlsWithAudit",
    ...traceExtra,
  };
}

function emitVpBaggageAuditToConsole(trace) {
  try {
    if (
      typeof import.meta !== "undefined" &&
      import.meta.env?.DEV &&
      import.meta.env?.MODE !== "test" &&
      trace?.flatAuthoritativeIlsMode &&
      trace?.vpBaggageLdsFareUsesDealCurrencyFx
    ) {
      emitVacationPackageExtraCurrencyAudit({
        extraType: trace.extraType,
        rawSupplierFareAmount: trace.rawSupplierFareInLdsDealCurrency,
        rawSupplierFareCurrency:
          trace.ldsDealFareCurrencyNormalized || "UNKNOWN",
        normalizedSupplierPortionIls: trace.normalizedSupplierPortionIls,
        supplierFareWasConverted: trace.supplierFareWasFxConverted,
        rawExtraAmount: trace.gcBaggageFlatRaw,
        rawExtraCurrency: trace.gcFlatInterpretationForVpBaggage,
        normalizedFlatPortionIls: trace.normalizedFlatPortionIls,
        flatWasConverted: trace.flatWasFxConverted,
        normalizedTotalIls: trace.normalizedTotalIls,
        wasConverted: trace.wasConvertedEitherLeg,
        source: trace.source,
        seqNum: trace.seqNum,
        dealId: trace.dealId,
      });
    }
  } catch {
    /* ignore */
  }
}

export function computeSupplementPriceIlsWithAudit(options) {
  const { totalIls, trace } = supplementPriceAuditSnapshot(options);
  emitVpBaggageAuditToConsole(trace);
  return { totalIls, trace };
}

export function computeVpBaggageTraceForLdsSupplementCard(supp, ctx) {
  const row = supp?.orig?.supp;
  const supplement =
    row && typeof row === "object"
      ? row
      : {
          type: supp?.type,
          desc: supp?.description,
          description: supp?.description,
        };
  const fareFromDeal = safeNum(row?.fare ?? supplement?.fare);
  const dealCur =
    ctx?.fareDetailsCurrency ??
    ctx?.dealFareCurrencyIso ??
    ctx?.vpLdsDealFareCurrencyIso ??
    null;

  return supplementPriceAuditSnapshot({
    fareUsd: fareFromDeal,
    currencyRateUsd:
      ctx?.currencyRateUsd != null
        ? safeNum(ctx.currencyRateUsd)
        : safeNum(ctx?.currencyRate?.USD ?? 1),
    baggageFeeFlatUsd:
      typeof ctx?.getBaggageFeeFlat === "function" ? ctx.getBaggageFeeFlat() : 0,
    supplement,
    baggageSupplierFareAuthoritativeIls: false,
    vpBaggageApplyLdsDealCurrencyFx: !!ctx?.vpBaggageApplyLdsDealCurrencyFx,
    vpLdsDealFareCurrencyIso: dealCur,
    exchangeRates: ctx?.currencyRate || null,
    traceExtra: {
      source: "computeVpBaggageTraceForLdsSupplementCard",
      seqNum: supp?.["@seqNum"],
      fltRef: supp?.flight?.fltRef,
      flightIndex: supp?.flight?.flightIndex,
      dealId: ctx?.dealId ?? null,
    },
  });
}

export function computeSupplementPriceIls(params) {
  return computeSupplementPriceIlsWithAudit(params).totalIls;
}
